import React, { FC, useState, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { fontSize } from '../../../styleHelpers/fontSizes';
import { colorStack } from '../../../styleHelpers/colors';
import { TextComponent } from '../../Common/Inputs/TextComponent';
import { IValue } from '../../../entities/IPickers';
import { Button } from '../../Common/Buttons/NewButton';
import { LabelWithEllipsis } from '../../Common/Label/Label';
import { IState } from '../../../reducers';
import { IProfileReducer } from '../../../reducers/profileReducer';
import { sendValidation, getCurrentUserProfileNew, checkFinalRegistrationRule } from '../../../actions/profileActions';
import { getCurrentUserContextOrganizations } from '../../../actions/contextActions';
import { IUserRegistrationValidation } from '../../../entities/IProfile';
import { media } from '../../../styleHelpers/breakpoint';
import { LcLogo } from '../../Common/CutomIcons/LcLogo';
import { IConfigReducer } from '../../../reducers/configReducer';
import { selectClientLogo } from '../../../tools/platformConfiguration';
import { MandatoryFieldStar } from '../../Common/MandatoryFieldStar/MandatoryFieldStar';
import { UsersPicker } from '../../Common/Pickers/UsersPicker';
import { LegalEntitiesPicker } from '../../Common/Pickers/LegalEntitiesPicker';
import { EProfileType } from '../../../entities/IGlobal';
import IntlMessage from '../../Common/IntlMessage';

type SendValidation = ReturnType<typeof sendValidation>;
type GetCurrentUserProfileNew = ReturnType<typeof getCurrentUserProfileNew>;
type GetCurrentUserContextOrganizations = ReturnType<typeof getCurrentUserContextOrganizations>;
type CheckFinalRegistrationRule = ReturnType<typeof checkFinalRegistrationRule>;

const Wrapper = styled.div`
    display: flex;
    width: 100%;
    font-family: 'Roboto', sans-serif;
    flex-direction: column;
    ${media.desktop`
        height: 100vh;
    `}
`;

const Content = styled.div`
    display: flex;
    height: 100%;
`;

const Header = styled.div`
    display: flex;
    background: ${colorStack.white};
    padding: 1rem;
    justify-content: space-between;
`;

const ImageSection = styled.div<{ image: string }>`
    background: url(${props => props.image});
    width: 50%;
    height: 100%;
    background-size: cover;
    background-position: bottom;
    display: none;
    ${media.desktop`
        display: block;
    `}
`;

const FormSection = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${colorStack.ligthGrey};
    padding: 1rem;
    ${media.desktop`
        width: 50%;
    `}
`;

const Row = styled.div`
    display: flex;
    justify-content: space-between;
    flex-direction: column;
    ${media.tablet`
        margin: 0 0 2rem 0;
        flex-direction: row;
    `}
    > div {
        margin: 0 0 2rem 0;
        ${media.tablet`
            width: 47%;
            margin: 0;
        `}
    }
`;

const FormInner = styled.div`
    flex-direction: column;
    width: 600px;
    display: flex;
`;

const ClientLogo = styled.img`
    display: block;
    margin-right: 1rem;
    height: 2rem;
`;

const Title = styled.span`
    font-size: ${fontSize[39]};
    font-weight: 500;
    margin: 0 0 1rem 0;
`;

const SubTitle = styled.span`
    font-size: ${fontSize[20]};
    font-weight: 300;
    margin: 0 0 3rem 0;
`;

const ButtonWrapper = styled.div`
    display: flex;
    justify-content: flex-start;
`;

const SearchWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

export const Registration: FC<React.PropsWithChildren<unknown>> = () => {
    const dispatch = useDispatch();
    const history = useNavigate();
    const { currentUserProfile, clientLogo } = useSelector<IState, IProfileReducer & IConfigReducer & { clientLogo: string }>(state => ({
        ...state.profile,
        ...state.config,
        clientLogo: selectClientLogo(state)
    }));
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [jobTitle, setJobTitle] = useState<string>('');
    const [manager, setManager] = useState<IValue[]>(undefined);
    const [legalEntity, setLegalEntity] = useState<IValue[]>(undefined);
    const [loading, setLoading] = useState<boolean>(false);
    const legalEntityRef = useRef<HTMLDivElement>(null);
    const managerRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        dispatch<CheckFinalRegistrationRule>(checkFinalRegistrationRule()).then(activeResponse => {
            if (currentUserProfile?.isProfileValidated || !activeResponse) {
                history('/');
            }
        });
        setFirstName(currentUserProfile?.firstName);
        setLastName(currentUserProfile?.lastName);
        setJobTitle(currentUserProfile?.jobTitle);
        if (currentUserProfile?.manager?.id) {
            setManager([{
                text: `${currentUserProfile?.manager?.firstName} ${currentUserProfile?.manager?.lastName}`,
                key: currentUserProfile?.manager?.id,
                data: currentUserProfile?.manager
            }]);
        }
        if (currentUserProfile?.cabinet) {
            setLegalEntity([{
                key: currentUserProfile.cabinet.id,
                text: currentUserProfile.cabinet.name,
                data: {...currentUserProfile.cabinet, type: EProfileType.LegalEntity}
            }]);
        }
    }, [currentUserProfile]);

    const changeFirstName = (value: string) => {
        setFirstName(value);
    };

    const changeLastName = (value: string) => {
        setLastName(value);
    };

    const changeJobTitle = (value: string) => {
        setJobTitle(value);
    };

    const selectManager = useCallback((value: IValue[]) => {
        setManager(value);
    }, []);

    const slectLegalEntity = useCallback((value: IValue[]) => {
        setLegalEntity(value);
    }, []);

    const sendValidationHandler = useCallback(() => {
        setLoading(true);
        const body: IUserRegistrationValidation = {
            firstName,
            lastName,
            jobTitle,
            cabinet: legalEntity?.[0].data,
            manager: manager?.[0].data
        };
        dispatch<SendValidation>(sendValidation(currentUserProfile?.id, body)).then(() => {
            setTimeout(() => {
                dispatch<GetCurrentUserContextOrganizations>(getCurrentUserContextOrganizations());
                dispatch<GetCurrentUserProfileNew>(getCurrentUserProfileNew()).then(() => {
                    setLoading(false);
                    history('/');
                });
            }, 2000);
        });
    }, [currentUserProfile, firstName, lastName, jobTitle, legalEntity, manager]);

    return (
        <Wrapper>
            <Header>
                <LcLogo width={32} />
                {clientLogo && (
                    <ClientLogo src={clientLogo} />
                )}
            </Header>
            <Content>
                <ImageSection image={require('../../../../wwwroot/assets/images/final-registration.png')} />
                <FormSection>
                    <FormInner>
                        <Title>
                            <IntlMessage id="register.final.title" />
                        </Title>
                        <SubTitle>
                            <IntlMessage id="register.final.subtext" />
                        </SubTitle>
                        <Row>
                            <TextComponent
                                className="lc-st-register-firstName"
                                value={firstName}
                                onChange={changeFirstName}
                                label={<IntlMessage id="register.final.firstName" />}
                                required
                            />
                            <TextComponent
                                className="lc-st-register-lastName"
                                value={lastName}
                                onChange={changeLastName}
                                label={<IntlMessage id="register.final.lastName" />}
                                required
                            />
                        </Row>
                        <Row>
                            <TextComponent
                                className="lc-st-register-jobTitleName"
                                value={jobTitle}
                                onChange={changeJobTitle}
                                label={<IntlMessage id="register.final.jobTitle" />}
                                required
                            />
                            <SearchWrapper className="lc-st-register-manager" ref={managerRef}>
                                <LabelWithEllipsis>
                                    <IntlMessage id="register.final.manager" />
                                    <MandatoryFieldStar />
                                </LabelWithEllipsis>
                                <UsersPicker
                                    value={manager}
                                    onSelectElement={selectManager}
                                    parentRef={managerRef}
                                />
                            </SearchWrapper>
                        </Row>
                        <Row ref={legalEntityRef}>
                            <SearchWrapper className="lc-st-register-legalEntity">
                                <LabelWithEllipsis>
                                    <IntlMessage id="register.finale.legalEntity" />
                                    <MandatoryFieldStar />
                                </LabelWithEllipsis>
                                <LegalEntitiesPicker
                                    value={legalEntity}
                                    onSelectElement={slectLegalEntity}
                                    hideButtons
                                    filteredCompanies
                                    parentRef={legalEntityRef}
                                />

                            </SearchWrapper>
                        </Row>
                        <ButtonWrapper>
                            <Button
                                onClick={sendValidationHandler}
                                type="button"
                                loading={loading}
                                disabled={!firstName || !lastName || !jobTitle || !legalEntity || !manager?.length}
                            >
                                <IntlMessage id="global.submit" />
                            </Button>
                        </ButtonWrapper>
                    </FormInner>

                </FormSection>
            </Content>

        </Wrapper>
    );
};
export default Registration;